/**
 * channel-chips.tsx — Hàng chip lọc theo nhóm kênh ("Tất cả", "Zalo cá nhân", "OA"…).
 *
 * Dùng chung cho: ô chọn tài khoản (cột lọc Hội thoại), menu "Kênh áp dụng"
 * của màn Train AI, và hàng lọc kênh trên danh sách hội thoại — cùng một khái
 * niệm thì cùng một hình dạng, người dùng học một lần.
 *
 * Một hàng duy nhất, tràn thì cuộn ngang: xuống dòng sẽ đẩy danh sách bên dưới
 * nhảy lên nhảy xuống mỗi khi số kênh thay đổi.
 */
import { useTranslation } from 'react-i18next'
import type { ChannelGroup, ChannelGroupId } from '@/lib/channel-groups'
import { cn } from '@/lib/utils'

export type ChannelFilterValue = ChannelGroupId | 'all'

export function ChannelChips({
  groups,
  value,
  onChange,
  className,
}: {
  /** Nhóm có tài khoản (xem `groupsPresent`). */
  groups: ChannelGroup[]
  value: ChannelFilterValue
  onChange: (v: ChannelFilterValue) => void
  className?: string
}) {
  const { t } = useTranslation('chanCatalog')
  const items: { id: ChannelFilterValue; label: string }[] = [
    { id: 'all', label: t('common:channels.all') },
    ...groups.map((g) => ({ id: g.id, label: g.label })),
  ]
  return (
    <div
      role="radiogroup"
      aria-label={t('common:channels.filterByChannel')}
      className={cn(
        // Thanh cuộn mảnh: vẫn thấy được là cuộn ngang, không chiếm chỗ.
        'flex items-center gap-1 overflow-x-auto whitespace-nowrap pb-0.5 [scrollbar-width:thin]',
        className,
      )}
      // Menu (Radix) bắt phím mũi tên/chữ để di chuyển giữa các mục; chip
      // không phải mục menu nên chặn lại cho khỏi nhảy tiêu điểm lung tung.
      // Chừa Escape để vẫn đóng được menu khi tiêu điểm đang ở chip.
      onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
    >
      {items.map((it) => {
        const active = value === it.id
        return (
          <button
            key={it.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(it.id)}
            className={cn(
              'shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {it.label}
          </button>
        )
      })}
    </div>
  )
}
